import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, ChevronRight, FileSpreadsheet, HeartHandshake, Link2, MapPin, QrCode, Target } from 'lucide-react';
import { motion, stagger } from 'framer-motion';
export const Route = createFileRoute('/_layout/about-adora-media/')({
    component: AboutPage,
    head: () => ({
        meta: [
            { title: 'About Adora Media — Campaign Analytics for Real-World Businesses' },
            {
                name: 'description',
                content: 'Adora Media builds campaign analytics tools for SMEs. We help businesses measure offline marketing — QR codes, flyers, menus, and signage — with clarity and simplicity.',
            },
            { property: 'og:title', content: 'About Adora Media — Campaign Analytics for Real-World Businesses' },
            {
                property: 'og:description',
                content: 'Adora Media builds campaign analytics tools for SMEs. We help businesses measure offline marketing — QR codes, flyers, menus, and signage — with clarity and simplicity.',
            },
        ],
    }),
});
const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
};
const fadeDelayed = (delay) => ({
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { delay, duration: 0.45 },
    },
});
function AboutPage() {
    return (<div className="min-h-dvh bg-background">
            {/* HERO */}
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16" initial="hidden" animate="visible" variants={{ visible: { transition: { delayChildren: stagger(0.08) } } }}>
                <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground w-fit">
                    <BarChart3 className="h-3.5 w-3.5"/>
                    Adora • About
                </motion.div>

                <motion.h1 className="text-3xl md:text-5xl font-bold leading-tight mt-4" variants={fadeUp}>
                    Campaign analytics for real-world businesses
                </motion.h1>

                <motion.p className="max-w-2xl text-muted-foreground mt-3" variants={fadeUp}>
                    Adora turns every QR code and short link into a measurable campaign. From menus, packaging, and
                    flyers to social posts and bio links, you see what actually moves people to take action.
                </motion.p>

                <motion.div className="flex flex-col sm:flex-row gap-2 pt-4" variants={fadeUp}>
                    <Button asChild size="lg">
                        <Link to="/sign-up" search={{ redirect: '/console' }} aria-label="Create an Adora account">
                            Start free
                        </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg">
                        <Link to="/sign-in" search={{ redirect: '/console' }}>
                            Sign in
                        </Link>
                    </Button>
                </motion.div>
            </motion.section>

            <Separator />

            {/* MISSION & VALUES */}
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-10 md:py-14 grid gap-6 md:grid-cols-3" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={{ visible: { transition: { delayChildren: stagger(0.12) } } }}>
                <motion.div variants={fadeUp} className="md:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                                <Target className="h-5 w-5 text-primary"/> Our mission
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-muted-foreground text-sm md:text-base">
                            We help small businesses, printers, and agencies treat every promotion as a real campaign —
                            with data they can understand and act on.
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div variants={fadeUp}>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
                                <HeartHandshake className="h-5 w-5 text-primary"/> What we value
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li>• Clarity over complexity</li>
                                <li>• Honest, simple pricing</li>
                                <li>• Data ownership and portability</li>
                                <li>• Built for real-world workflows</li>
                            </ul>
                        </CardContent>
                    </Card>
                </motion.div>
            </motion.section>

            {/* WHAT ADORA DOES */}
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-10 md:py-14" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={{ visible: { transition: { delayChildren: stagger(0.1) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    What Adora does
                </motion.h2>
                <motion.p className="text-muted-foreground mt-2 max-w-2xl" variants={fadeUp}>
                    Adora makes offline and online campaigns measurable — without adding complexity.
                </motion.p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[
            {
                icon: <Link2 className="h-5 w-5"/>,
                title: 'Campaign tracking',
                body: 'Create a campaign for each promotion and track performance by placement or idea.',
            },
            {
                icon: <QrCode className="h-5 w-5"/>,
                title: 'Smart QR & links',
                body: 'QR codes and short links that act as measurable entry points.',
            },
            {
                icon: <BarChart3 className="h-5 w-5"/>,
                title: 'Clear analytics',
                body: 'See scans, unique visitors, and destination performance in one place.',
            },
            {
                icon: <MapPin className="h-5 w-5"/>,
                title: 'Offline-first',
                body: 'Designed for printed materials, signage, and physical distribution.',
            },
            {
                icon: <FileSpreadsheet className="h-5 w-5"/>,
                title: 'Shareable reports',
                body: 'Export insights for managers, partners, or clients.',
            },
            {
                icon: <Target className="h-5 w-5"/>,
                title: 'Growing insight',
                body: 'Deeper understanding of what works — and why.',
            },
        ].map((f, i) => (<motion.div key={i} variants={fadeDelayed(i * 0.08)}>
                            <Feature icon={f.icon} title={f.title}>
                                {f.body}
                            </Feature>
                        </motion.div>))}
                </div>
            </motion.section>

            <Separator />

            {/* TIMELINE */}
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-10 md:py-14" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }} variants={{ visible: { transition: { delayChildren: stagger(0.1) } } }}>
                <motion.h2 className="text-2xl md:text-3xl font-semibold" variants={fadeUp}>
                    Product timeline
                </motion.h2>

                <div className="mt-6 space-y-6">
                    <motion.div variants={fadeUp}>
                        <TimelineItem date="2025" title="Campaign analytics foundation" text="Campaigns, smart links, QR tracking, and reporting for real-world use."/>
                    </motion.div>

                    <motion.div variants={fadeUp}>
                        <TimelineItem date="Next" title="Deeper offline insight" text="Better comparisons, clearer signals, and improved performance visibility."/>
                    </motion.div>

                    <motion.div variants={fadeUp}>
                        <TimelineItem date="2026" title="A/B testing & experimentation" text="Structured experiments to compare placements, creatives, and offline strategies."/>
                    </motion.div>
                </div>
            </motion.section>

            {/* CTA */}
            <motion.section className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
                <Card>
                    <CardContent className="p-6 md:p-10 flex flex-col md:flex-row justify-between gap-6">
                        <div>
                            <h3 className="text-xl md:text-2xl font-semibold">
                                Start with a few campaigns. Grow into real offline analytics.
                            </h3>
                            <p className="mt-2 text-muted-foreground max-w-xl">
                                No long contracts. Measure what works before scaling further.
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row gap-2">
                            <Button asChild size="lg">
                                <Link to="/sign-up" search={{ redirect: '/console' }}>
                                    Create free account
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg">
                                <Link to="/sign-in" search={{ redirect: '/console' }}>
                                    Sign in
                                    <ChevronRight className="ml-1.5 h-4 w-4"/>
                                </Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.section>
        </div>);
}
const Feature = ({ icon, title, children }) => (<Card className="h-full transition hover:shadow-md hover:bg-muted/30">
        <CardHeader>
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </span>
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{children}</CardContent>
    </Card>);
Feature.displayName = 'Feature';
const TimelineItem = ({ date, title, text }) => (<div className="relative pl-6">
        <div className="absolute left-0 top-1.5 h-3 w-3 rounded-full bg-primary/80"/>
        <div className="text-xs text-muted-foreground">{date}</div>
        <div className="font-medium">{title}</div>
        <div className="text-sm text-muted-foreground">{text}</div>
    </div>);
